import { TZDate } from '@date-fns/tz'
import { format, isValid, parse } from 'date-fns'
import { intersection, isEmpty, union } from 'lodash-es'

import { EMPTY_LIST } from '../../../../../constants/common'
import {
  DATE_API_DATA_FORMAT,
  DATE_API_DATA_FORMAT_SHORT,
} from '../../../../../constants/date'
import { DATE_FILTER_TYPES } from '../../../../../constants/filter'
import { createNowDateInUkTimezone } from '../../../../../utils/date'
import { getDateEndParam, getDateStartParam } from '../../../../../utils/filter'

import {
  VIEWINGS_FILTER_DAY_PARTS_PATTERN,
  VIEWINGS_FILTER_PARAM_LIST,
} from './useRentalPortalViewingsFilters.constants'

function getViewingsFilterHostParamList({ name, type }) {
  return DATE_FILTER_TYPES.includes(type)
    ? [getDateStartParam(name), getDateEndParam(name)]
    : [name]
}

function getViewingsDescriptorParamList(filter) {
  return intersection(
    [filter.query_param].flat(),
    getViewingsFilterHostParamList(filter)
  )
}

function getViewingsFilterTypeByParam(availableFilters) {
  return Object.fromEntries(
    (availableFilters ?? EMPTY_LIST).flatMap(filter =>
      getViewingsDescriptorParamList(filter).map(param => [param, filter.type])
    )
  )
}

function getViewingsFilterOptionValuesByParam(availableFilters) {
  return Object.fromEntries(
    (availableFilters ?? EMPTY_LIST)
      .filter(filter => filter.options)
      .flatMap(filter =>
        getViewingsDescriptorParamList(filter).map(param => [
          param,
          filter.options.map(option => String(option.value)),
        ])
      )
  )
}

function getViewingsAppliedFilterValue({
  optionValues,
  timezone,
  type,
  value,
}) {
  if (DATE_FILTER_TYPES.includes(type)) {
    return getViewingsFilterDayValue(value, timezone)
  }

  if (optionValues) {
    return optionValues.includes(String(value)) ? value : null
  }

  return value
}

export function getViewingsDescribedFilterParamList(availableFilters) {
  return (availableFilters ?? EMPTY_LIST).flatMap(
    getViewingsDescriptorParamList
  )
}

export function getViewingsClearedFilterParamList(availableFilters) {
  return isEmpty(availableFilters)
    ? EMPTY_LIST
    : union(
        VIEWINGS_FILTER_PARAM_LIST,
        availableFilters.flatMap(filter => [
          ...[filter.query_param].flat(),
          ...getViewingsFilterHostParamList(filter),
        ])
      )
}

export function getViewingsFilterDayValue(value, timezone) {
  const parts = String(value).match(VIEWINGS_FILTER_DAY_PARTS_PATTERN)

  if (!parts) {
    return null
  }

  const [statedDay, year, month, day] = parts

  const date = new TZDate(
    Number(year),
    Number(month) - 1,
    Number(day),
    timezone
  )

  return isValid(date) && format(date, DATE_API_DATA_FORMAT_SHORT) === statedDay
    ? statedDay
    : null
}

export function getViewingsFilterDayInstant(value, timezone) {
  const date = parse(
    value,
    DATE_API_DATA_FORMAT_SHORT,
    createNowDateInUkTimezone(timezone)
  )

  return isValid(date) ? format(date, DATE_API_DATA_FORMAT) : value
}

export function getViewingsFilterValues({
  availableFilters,
  searchParams,
  timezone,
}) {
  const typeByParam = getViewingsFilterTypeByParam(availableFilters)

  const optionValuesByParam =
    getViewingsFilterOptionValuesByParam(availableFilters)

  return Object.fromEntries(
    getViewingsDescribedFilterParamList(availableFilters)
      .filter(param => searchParams.has(param))
      .map(param => [
        param,
        getViewingsAppliedFilterValue({
          optionValues: optionValuesByParam[param],
          timezone,
          type: typeByParam[param],
          value: searchParams.get(param),
        }),
      ])
      .filter(([, value]) => value !== null)
  )
}

export function getViewingsFilterParams({
  availableFilters,
  filterValues,
  timezone,
}) {
  const typeByParam = getViewingsFilterTypeByParam(availableFilters)

  return Object.entries(filterValues).flatMap(([param, value]) =>
    [value]
      .flat()
      .map(entry =>
        DATE_FILTER_TYPES.includes(typeByParam[param])
          ? getViewingsFilterDayValue(entry, timezone)
          : entry
      )
      .filter(Boolean)
      .map(entry => [param, entry])
  )
}

export function getViewingsFilterBarValues({
  availableFilters,
  filterValues,
  timezone,
}) {
  const typeByParam = getViewingsFilterTypeByParam(availableFilters)

  return Object.fromEntries(
    Object.entries(filterValues).map(([param, value]) => [
      param,
      DATE_FILTER_TYPES.includes(typeByParam[param])
        ? getViewingsFilterDayInstant(value, timezone)
        : value,
    ])
  )
}
